/**
 * Reviews → Settings → Timing: the send-time dropdown and the timing blob the form edits.
 *
 * ⛔ IMPORT-FREE ON PURPOSE. cypress/node-tests/reviews-settings-timing-exclusions.test.js
 * strips the `export`s and executes THIS file, so the test can never drift from what ships.
 * Only `export const` / `export function` here — no imports, no default export.
 *
 * The contract (REVIEWS_SETTINGS.timing, owned by reviews-service lib/settings.js):
 *   { mode: 'delay' | 'timeOfDay', sendTime: 'HH:mm', delayValue, delayUnit }
 * sendTime is on the 30-minute grid, 08:00–20:30. A visit marked complete at or after
 * sendTime gets its request at sendTime the NEXT day.
 */

export const SEND_TIME_MIN = '08:00';
export const SEND_TIME_MAX = '20:30';
export const SEND_TIME_STEP_MIN = 30;
export const DEFAULT_SEND_TIME = '19:00';

// The old fixed "6–7 pm" option stored its window start. Mint of Emerson's 18:00 must read
// back as 6:00 PM, not jump to the 7:00 PM default — so an unreadable windowStart falls back
// to 18:00 (what the old option always meant), never to DEFAULT_SEND_TIME.
const LEGACY_WINDOW_FALLBACK = '18:00';

function pad2(n) {
  return n < 10 ? '0' + n : String(n);
}

function toMinutes(hhmm) {
  if (typeof hhmm !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

function fromMinutes(total) {
  return pad2(Math.floor(total / 60)) + ':' + pad2(total % 60);
}

function labelFor(total) {
  const h24 = Math.floor(total / 60);
  const min = total % 60;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return h12 + ':' + pad2(min) + ' ' + suffix;
}

/** 26 options: { value: '08:00', text: '8:00 AM' } … { value: '20:30', text: '8:30 PM' }. */
export const SEND_TIME_OPTIONS = (function buildSendTimeOptions() {
  const out = [];
  const start = toMinutes(SEND_TIME_MIN);
  const end = toMinutes(SEND_TIME_MAX);
  for (let t = start; t <= end; t += SEND_TIME_STEP_MIN) {
    out.push({ value: fromMinutes(t), text: labelFor(t) });
  }
  return out;
})();

/** True only for an exact 'HH:mm' on the 30-min grid inside 08:00–20:30 ('8:00' is NOT valid). */
export function isValidSendTime(value) {
  if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) return false;
  return SEND_TIME_OPTIONS.some((o) => o.value === value);
}

/** Round down to the 30-min grid and clamp into 08:00–20:30. null if unparseable. */
export function snapSendTime(value) {
  const total = toMinutes(value);
  if (total === null) return null;
  const lo = toMinutes(SEND_TIME_MIN);
  const hi = toMinutes(SEND_TIME_MAX);
  const snapped = total - (total % SEND_TIME_STEP_MIN);
  return fromMinutes(Math.min(hi, Math.max(lo, snapped)));
}

/**
 * timingForForm(timing) → a NEW { mode, sendTime, delayValue, delayUnit }
 *
 * A defensive mirror of the server's old-blob rule (reviews-service mergeWithDefaults). The
 * server already converts, but an older API response — or a Save from before the service
 * deploy — can still hand back mode 'eveningWindow', and the form has no radio for it: the
 * office would see NEITHER timing option selected and a Save would drop the time.
 *
 *   - mode 'eveningWindow' → 'timeOfDay', sendTime snapped from windowStart (fallback 18:00)
 *   - any other non-'delay' / missing mode → 'timeOfDay' (the server default)
 *   - missing or off-grid sendTime → 19:00
 *   - delayValue / delayUnit default to 2 / 'hours' only when ABSENT, so a bad value the
 *     office typed still reaches validate() instead of being silently replaced.
 */
export function timingForForm(timing) {
  const t = timing && typeof timing === 'object' ? timing : {};
  const absent = (v) => v === undefined || v === null;

  let mode;
  let sendTime;
  if (t.mode === 'eveningWindow') {
    mode = 'timeOfDay';
    sendTime = snapSendTime(t.windowStart) || LEGACY_WINDOW_FALLBACK;
  } else {
    mode = t.mode === 'delay' ? 'delay' : 'timeOfDay';
    sendTime = isValidSendTime(t.sendTime) ? t.sendTime : DEFAULT_SEND_TIME;
  }

  return {
    mode,
    sendTime,
    delayValue: absent(t.delayValue) ? 2 : t.delayValue,
    delayUnit: absent(t.delayUnit) ? 'hours' : t.delayUnit,
  };
}
